import { useEffect } from "react";
import { PlotType } from "../../../../data/plot-types";
import { RangeSelection } from "xlcommon/src/excel/excel-grid-utils";
import { useChart } from "../../../hooks/plots/useCentralViz";
import { Axes, Chart, Estimators, IntervalOptions, Orientation, IntervalValueMap } from "../MVCShared/types";
import {
  DataRangeAttr,
  DividerAttr,
  DropdownAttr,
  HeadingAttr,
  LabelAttr,
  CollapsibleAttr,
  SpinnerAttr,
  ColorPickerAttr,
  PaletteAttr,
  CheckBoxAttr,
  AxisDropdownAttr,
  LegendAttr,
  ErrorKws,
  GridlinesAttr,
  SingleChartBorders,
  Origin,
} from "../MVCShared/PlotAttributes";
import { buildCode, buildReactFromAttrs, CodeBuilder } from "../MVCShared/CodeBuilder";
import {
  dependencyEqualsValue,
  dependencyNotEqualsValue,
  dependencyInValues,
  fetchHeaders,
} from "../MVCShared/PlotGeneratorUtils";
import { BarDesign as IBarDesign, BarSetup } from "../../../../taskpane/hooks/plots/useBar";

const BarContext = (): Chart => {
  const { setup, design, common, updateDesign, updateSetup, updateCommon, setCodeFragments } = useChart<
    BarSetup,
    IBarDesign
  >(PlotType.bar);

  useEffect(() => {
    (async () => {
      const cb = await buildCode(common, [...barChart.baseAttrs, ...barChart.designAttrs]);
      setCodeFragments(cb);
    })();
  }, [setup, design, common]);

  useEffect(() => {
    (async () => {
      await fetchHeaders(common.inputData, common.hasHeaders, updateSetup);
    })();
  }, [common.hasHeaders, common.inputData]);

  const orientation = DropdownAttr({
    value: setup.orientation,
    onChange: (_, data) => {
      const prevX: string = setup.xAxis;
      const prevY: string = setup.yAxis;
      updateSetup({
        orientation: data.optionValue,
        xAxis: prevY,
        yAxis: prevX,
      });
      const prevXAxisLabel: string = design.xAxisLabel;
      const prevYAxisLabel: string = design.yAxisLabel;
      updateDesign({
        xAxisLabel: prevYAxisLabel,
        yAxisLabel: prevXAxisLabel,
      });
    },
    label: "Orientation",
    options: ["Horizontal", "Vertical"],
    codeKey: "orient",
    codeValueMap: Orientation,
    dataTestID: "orientation",
    codeRequiresInteraction: true,
  });

  const errorBar = DropdownAttr({
    value: setup.errorBar,
    label: "Error Bar",
    dataTestID: "error-bar",
    placeholder: "No error bars",
    onChange: (_, data) => updateSetup({ errorBar: data.optionValue }),
    codeRequiresInteraction: false,
    options: IntervalOptions,
  });
  errorBar.getCode = (code: CodeBuilder) => {
    // The intervals will override the errorbar attribute if set
    if (setup.errorBar === "No error bars") {
      code.plotAttrs.push("errorbar=None");
    }
  };

  const estimator = DropdownAttr({
    value: setup.estimator,
    onChange: (_, data) => updateSetup({ estimator: data.optionValue }),
    label: "Estimator",
    options: ["Mean", "Median", "Max", "Min", "Sum"],
    placeholder: "Mean",
    codeKey: "estimator",
    codeValueMap: Estimators,
  });

  // Two intervals are used because they have different max values and initial setpoints
  // based on the error bar chosen. Only one will be shown at a time because the
  // visibleDependencies are mutually exclusive.
  const primaryInterval = SpinnerAttr({
    label: "Interval",
    value: setup.primaryInterval,
    step: 1,
    max: 100,
    min: 0,
    suffix: "%",
    onChange: (data: number) => updateSetup({ primaryInterval: data }),
    visibleDependencies: [dependencyInValues(errorBar, ["Confidence Interval", "Percentage Interval"])],
    enabledDependencies: [dependencyInValues(errorBar, ["Confidence Interval", "Percentage Interval"])],
    dataTestID: "interval",
  });
  primaryInterval.getCode = (code: CodeBuilder) => {
    code.plotAttrs.push(`errorbar=('${IntervalValueMap[setup.errorBar]}', ${setup.primaryInterval})`);
  };

  const secondaryInterval = SpinnerAttr({
    label: "Interval",
    value: setup.secondInterval,
    step: 1,
    max: Infinity,
    suffix: "%",
    onChange: (data: number) => updateSetup({ secondInterval: data }),
    visibleDependencies: [dependencyInValues(errorBar, ["Standard Error", "Standard Deviation"])],
    enabledDependencies: [dependencyInValues(errorBar, ["Standard Error", "Standard Deviation"])],
  });
  secondaryInterval.getCode = (code: CodeBuilder) => {
    code.plotAttrs.push(`errorbar=('${IntervalValueMap[setup.errorBar]}', ${setup.secondInterval})`);
  };

  const Legend = LegendAttr({
    value: design.legendPosition,
    onChange: (_, data) => updateDesign({ legendPosition: data.optionValue }, "Best"),
  });

  const colorBy = AxisDropdownAttr({
    value: setup.colorBy,
    onChange: (_, data) => updateSetup({ colorBy: data.optionValue }, "--None--"),
    label: "Color By",
    options: ["--None--", ...setup.headers],
    placeholder: "--None--",
    codeKey: "hue",
    hasHeaders: common.hasHeaders,
  });

  const palette = PaletteAttr({
    value: design.palette,
    onChange: (_, data) => {
      updateDesign({ palette: data.optionText });
    },
    codeKey: "palette",
    placeholder: "Accent",
    visibleDependencies: [dependencyNotEqualsValue(colorBy, "")],
    codeRequiresInteraction: true,
    dataTestID: "palette",
  });

  const dodge = CheckBoxAttr({
    label: "Overlapping groups",
    value: design.dodge,
    onChange: (_, e) => updateDesign({ dodge: e.checked }),
    codeKey: "dodge",
    codeRequiresInteraction: true,
    dataTestID: "dodge",
  });
  dodge.getCode = (code: CodeBuilder) => {
    if (design.dodge.isUpdated) {
      // Invert the value since the checkbox is for overlapping groups
      const dodge = design.dodge.value === true ? "False" : "True";
      code.plotAttrs.push(`dodge=${dodge}`);
    }
  };

  const alpha = SpinnerAttr({
    label: "Opacity",
    value: design.alpha,
    step: 0.1,
    min: 0,
    max: 1,
    onChange: (data: number) => updateDesign({ alpha: data }),
    codeKey: "alpha",
    dataTestID: "alpha",
    codeRequiresInteraction: true,
  });

  const origin = Origin({
    label: "Include Origin",
    yValue: setup.yLim,
    onChange: (_, e) => updateSetup({ yLim: e.checked }),
    codeRequiresInteraction: true,
  });

  const barChart: Chart = {
    baseAttrs: [
      DataRangeAttr({
        inputData: common.inputData,
        onChangeSelection: (newSelection: RangeSelection) => updateCommon({ inputData: newSelection }),
      }),
      CheckBoxAttr({
        label: "Has headers",
        value: common.hasHeaders,
        onChange: (_, e) => updateCommon({ hasHeaders: e.checked }),
        dataTestID: "headers",
      }),
      orientation,
      DividerAttr(),
      HeadingAttr({ title: "Data", tooltip: "Select data cells and parameters" }),
      AxisDropdownAttr({
        value: setup.xAxis,
        onChange: (_, data) => {
          updateSetup({ xAxis: data.optionValue }, "--Select--");
          updateDesign({ xAxisLabel: data.optionValue }, "--Select--");
        },
        dataTestID: "x-axis",
        label: "X-Axis",
        options: ["--Select--", ...setup.headers],
        codeKey: "x",
        hasHeaders: common.hasHeaders,
      }),
      AxisDropdownAttr({
        value: setup.yAxis,
        onChange: (_, data) => {
          updateSetup({ yAxis: data.optionValue }, "--Select--");
          updateDesign({ yAxisLabel: data.optionValue }, "--Select--");
        },
        dataTestID: "y-axis",
        label: "Y-Axis",
        options: ["--Select--", ...setup.headers],
        codeKey: "y",
        hasHeaders: common.hasHeaders,
      }),
      origin,
      estimator,
      colorBy,
      DividerAttr(),
      CollapsibleAttr({
        collapsed: setup.isCollapsed,
        label: "Error",
        tooltip: "Measure error in data",
        toggle: () => {
          updateSetup({ isCollapsed: !setup.isCollapsed });
        },
        children: [
          errorBar,
          primaryInterval,
          secondaryInterval,
          SpinnerAttr({
            label: "Bootstrap Samples",
            value: setup.bootstrapSamples,
            step: 100,
            min: 0,
            max: 2 ** 32 - 1,
            onChange: (data: number) => updateSetup({ bootstrapSamples: data }),
            codeKey: "n_boot",
            dataTestID: "bootstrap",
            codeRequiresInteraction: true,
            visibleDependencies: [dependencyNotEqualsValue(errorBar, "No error bars")],
          }),
          SpinnerAttr({
            label: "Seed",
            value: setup.seed,
            step: 1,
            max: 2 ** 32 - 1,
            min: 0,
            onChange: (data: number) => updateSetup({ seed: data }),
            codeKey: "seed",
            codeRequiresInteraction: true,
            dataTestID: "seed",
            visibleDependencies: [dependencyNotEqualsValue(errorBar, "No error bars")],
          }),
        ],
      }),
      DividerAttr(),
      HeadingAttr({
        title: "Axes",
        tooltip: "Set scale for axes",
      }),
      DropdownAttr({
        value: setup.xAxisScale,
        onChange: (_, data) => updateSetup({ xAxisScale: data.optionValue }),
        label: "X-Axis Scale",
        options: ["Linear", "Log"],
        placeholder: "Linear",
        callKey: "xscale",
        codeValueMap: Axes,
        visibleDependencies: [dependencyEqualsValue(orientation, "Vertical")],
      }),
      DropdownAttr({
        value: setup.yAxisScale,
        onChange: (_, data) => updateSetup({ yAxisScale: data.optionValue }),
        label: "Y-Axis Scale",
        options: ["Linear", "Log"],
        placeholder: "Linear",
        callKey: "yscale",
        codeValueMap: Axes,
        visibleDependencies: [dependencyEqualsValue(orientation, "Horizontal")],
      }),
      DividerAttr(),
    ],
    designAttrs: [
      LabelAttr({
        value: design.plotTitle,
        placeholder: "Title",
        label: "Title",
        codeKey: "title",
        onChange: (event) => updateDesign({ plotTitle: event.currentTarget.value }),
      }),
      LabelAttr({
        value: design.xAxisLabel,
        placeholder: "Defaults header",
        label: "X-Axis Label",
        codeKey: "xlabel",
        dataTestID: "x-axis-label",
        onChange: (event) => updateDesign({ xAxisLabel: event.currentTarget.value }),
      }),
      LabelAttr({
        value: design.yAxisLabel,
        placeholder: "Defaults header",
        label: "Y-Axis Label",
        codeKey: "ylabel",
        dataTestID: "y-axis-label",
        onChange: (event) => updateDesign({ yAxisLabel: event.currentTarget.value }),
      }),
      DividerAttr(),
      CollapsibleAttr({
        collapsed: design.borderCollapsed,
        label: "Border",
        toggle: () => {
          updateDesign({ borderCollapsed: !design.borderCollapsed });
        },
        children: [
          SingleChartBorders({
            label: "Top",
            value: design.topSpine,
            onChange: (_, e) => updateDesign({ topSpine: e.checked }),
            callKey: "top",
          }),
          SingleChartBorders({
            label: "Right",
            value: design.rightSpine,
            onChange: (_, e) => updateDesign({ rightSpine: e.checked }),
            callKey: "right",
          }),
          SingleChartBorders({
            label: "Bottom",
            value: design.bottomSpine,
            onChange: (_, e) => updateDesign({ bottomSpine: e.checked }),
            callKey: "bottom",
            lim: setup.yLim,
          }),
          SingleChartBorders({
            label: "Left",
            value: design.leftSpine,
            onChange: (_, e) => updateDesign({ leftSpine: e.checked }),
            callKey: "left",
          }),
        ],
      }),
      DividerAttr(),
      CollapsibleAttr({
        collapsed: design.gridlinesCollapsed,
        label: "Gridlines",
        toggle: () => {
          updateDesign({ gridlinesCollapsed: !design.gridlinesCollapsed });
        },
        children: [
          GridlinesAttr({
            majorHorizontal: design.majorHorizontal,
            majorVertical: design.majorVertical,
            minorHorizontal: design.minorHorizontal,
            minorVertical: design.minorVertical,
            onChange: (key, event) => {
              updateDesign({ [key]: event.checked });
            },
          }),
        ],
      }),
      DividerAttr(),
      HeadingAttr({ title: "Axes Label Rotation" }),
      SpinnerAttr({
        label: "X-Ticks",
        value: design.xticks,
        step: 5,
        max: 180,
        min: -180,
        onChange: (data: number) => updateDesign({ xticks: data }),
        callKey: "xticks",
        suffix: "°",
        codeRequiresInteraction: true,
      }),
      SpinnerAttr({
        label: "Y-Ticks",
        value: design.yticks,
        step: 5,
        max: 180,
        min: -180,
        onChange: (data: number) => updateDesign({ yticks: data }),
        callKey: "yticks",
        suffix: "°",
        codeRequiresInteraction: true,
      }),
      DividerAttr(),
      ColorPickerAttr({
        value: design.color,
        label: "Color",
        onChange: (hexColor) => updateDesign({ color: `#${hexColor}` }),
        codeKey: "color",
        codeRequiresInteraction: true,
      }),
      CheckBoxAttr({
        value: design.fill,
        onChange: (_, e) => updateDesign({ fill: e.checked }),
        label: "Fill",
        codeKey: "fill",
        codeRequiresInteraction: true,
      }),
      palette,
      DividerAttr(),
      HeadingAttr({ title: "Bar Formatting" }),
      SpinnerAttr({
        label: "Width",
        value: design.width,
        step: 0.1,
        max: 2 ** 32 - 1,
        min: 0,
        onChange: (data: number) => updateDesign({ width: data }),
        codeKey: "width",
        codeRequiresInteraction: true,
      }),
      dodge,
      alpha,
      DividerAttr(),
      HeadingAttr({ title: "Legend", tooltip: "Legend only visible for some combinations of inputs" }),
      Legend,
      DividerAttr(),
      CollapsibleAttr({
        collapsed: design.isCollapsed,
        label: "Error",
        toggle: () => {
          updateDesign({ isCollapsed: !design.isCollapsed });
        },
        children: [
          ErrorKws({
            value: design.errorColor,
            spinnerValue: design.errorBarWidth,
            onValueChange: (data) => {
              updateDesign({ errorBarWidth: data });
            },
            onChange: (color) => updateDesign({ errorColor: `#${color}` }),
            colorValue: design.errorColor,
            enabledDependencies: [dependencyNotEqualsValue(errorBar, "No error bars")],
            spinnerReqiuiresInteraction: true,
            colorRequiresInteraction: true,
          }),
          SpinnerAttr({
            label: "Error Bar Cap Width",
            value: design.errorBarCapWidth,
            step: 0.1,
            max: 1,
            min: 0,
            onChange: (data: number) => updateDesign({ errorBarCapWidth: data }),
            codeKey: "capsize",
            dataTestID: "capsize",
            codeRequiresInteraction: true,
            enabledDependencies: [dependencyNotEqualsValue(errorBar, "No error bars")],
          }),
        ],
      }),
    ],
  };
  return barChart;
};

const BarForm = () => {
  const barChart = BarContext();
  return buildReactFromAttrs(barChart.baseAttrs, 130);
};

export const BarDesign = () => {
  const barDesign = BarContext();
  return buildReactFromAttrs(barDesign.designAttrs, 140);
};

export default BarForm;
